/**
 * 6 - Knock Out Enemy Radar Array (03 §2): 3-6 antennas (half is success, all is the bonus)
 * behind a water barrier that spans the map - a frozen river with weak ice in the arctic, a
 * river or wadi with fords elsewhere - held by an elite garrison, on a tight clock.
 */

import { HAZARDS } from '../../sim/tuning';
import type { Point } from '../types';
import { checker, clutter, guardAtDoor, guardsAbout, required, structure, targetsOf } from './helpers';
import type { ArchetypeSpec } from './types';

const radarsFor = (threat: number): number => Math.min(6, 3 + Math.floor((threat - 1) / 3));

export const radarArray: ArchetypeSpec = {
  id: 'radar-array',
  name: 'KNOCK OUT ENEMY RADAR ARRAY',
  nativeBiome: 'arctic',
  baseThreat: 5,
  lengthTiles: 160,
  // "The clock is tight: drop the pods far north and run light."
  clockScale: 0.85,
  startAlert: 0,
  garrison: 'elite',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [54, 62], d: [30, 34] },
  river: true,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    const count = Math.min(6, radarsFor(g.threat) + g.rng.int(2));
    const ids: number[] = [];
    for (let i = 0; i < count; i++) {
      ids.push(required(g, 'radar', { target: true, minDist: 9, from: placed, margin: 1.2 }).id);
    }
    const hut = structure(g, 'hut', { minDist: 7, from: placed });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    if (hut) guardAtDoor(g, hut);
    guardsAbout(g, 2 + g.rng.int(2));
    return {
      objective: {
        kind: 'destroyTargets', label: 'DESTROY RADARS', targetPropIds: ids, minFraction: 0.5,
        rules: [{ type: 'recall', mode: 'normal' }],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('radar-array');
    const radars = targetsOf(def, 'radar');
    check(radars.length >= 3 && radars.length <= 6, `needs 3-6 radar antennas, has ${radars.length}`);
    check(
      def.objective.kind === 'destroyTargets' && def.objective.minFraction === 0.5,
      'half the antennas must count as success',
    );
    check(def.spawns.some((s) => s.kind === 'elite'), 'the garrison must be elite');
    const river = def.waterBarriers[0];
    check(river !== undefined, 'needs a water barrier');
    if (!river) return out;
    const xs = river.course.points.map((p) => p[0]);
    check(Math.min(...xs) <= 0 && Math.max(...xs) >= def.sizeX, 'the river must span the full width');
    check(river.crossings.length >= 1, 'the river needs at least one reliable crossing');
    check(def.biome !== 'arctic' || river.surface === 'ice', 'an arctic river is frozen');
    check(river.surface === 'ice' || river.weakIce.length === 0, 'only ice can be weak');
    check(def.biome !== 'arctic' || river.weakIce.length >= 3, 'a frozen river needs visibly weak ice');
    for (const c of river.crossings) {
      check(
        !river.weakIce.some((w) => Math.abs(w.x - c) < w.r + HAZARDS.crossingHalfWidth),
        `weak ice on the crossing at x = ${c}`,
      );
    }
    const zs = river.course.points.map((p) => p[1]);
    check(
      Math.max(...zs) < def.dropBand.z0 && Math.min(...zs) > def.goalZone.z1,
      'the river must lie between the drop band and the goal',
    );
    return out;
  },
};
